import React from 'react';
import { withRouter } from 'react-router';
import { Paper, withStyles, Button } from '@material-ui/core';
import { IFieldsProps } from './QuickLinks.properties';
import styles from './QuickLinks.styles';
import { ArrowBack, CheckCircle, Cancel as CancelIcon } from '@markinson/uicomponents-v2/SvgIcons/';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import QuickLinksGrid from './QuickLinksGrid';
import FieldsGrid from './FieldsGrid';
import { useDMEContextSelector } from '../DashboardMaintenanceEnquiry.context';
import ToggleButton from 'components/common/ToggleButton';
import { useSetDashboardQuickLinksQueryData, useSaveDashboardQuickLinks, useGetDMEDashboardDetailsQueryData } from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';

const QuickLinks = (props: IFieldsProps) => {
    const { classes, selectedDashboardType, selectedDashboardId, history, changeConfirmationDialog } = props;

    const [quickLinksDataState, setQuickLinksDataState] = React.useState(null);
    const [isQuickLinksEnabled, setIsQuickLinksEnabled] = React.useState<boolean | undefined>(undefined);
    const { setActionBar } = React.useContext(ActionBarContext);
    const firstButtonRef = React.useRef<HTMLButtonElement>(null);
    const lastButtonRef = React.useRef<HTMLButtonElement>(null);
    const fieldsGridInnerRef = React.useRef(null);
    const quickLinksGridInnerRef = React.useRef(null);
    const params = React.useMemo(() => (new URLSearchParams(location.search)), [location.search]);
    const dashboardId = Number(params.get('DashboardId')) || useDMEContextSelector<'DashboardId'>((state) => state.DashboardId);
    const setDashboardQuickLinksQueryData = useSetDashboardQuickLinksQueryData();
    const { mutateAsync: saveDashboardQuickLinksMutateAsync } = useSaveDashboardQuickLinks();
    const getDashboardDetails = useGetDMEDashboardDetailsQueryData();
    const cachedDashboardDetails = getDashboardDetails(Number(selectedDashboardId));

    const dashboardType = React.useMemo(
        () => (cachedDashboardDetails as any)?.DashboardDetail?.inlineObject?.DashboardType ?? selectedDashboardType,
        [cachedDashboardDetails, selectedDashboardType]
    );

    React.useEffect(
        () => {
            if (
                quickLinksDataState?.EnableQuickLinks !== undefined &&
                quickLinksDataState?.EnableQuickLinks !== null
              ) {
                setIsQuickLinksEnabled(quickLinksDataState.EnableQuickLinks);
              }
        },
        [quickLinksDataState]
    );

    const OnDiscardDashboardQuickLinksChanges = async () => {
        changeConfirmationDialog({
            open: true,
            title: 'Discard changes',
            message: 'Are you sure you want to continue?',
            okLabel: 'Discard',
            onCancel: () => {
                // do nothing, just close the dialog
            },
            onOk: handleDiscardOnOkConfirmation
        });
    };

    const handleDiscardOnOkConfirmation = async () => {
        await quickLinksGridInnerRef?.current?.refetch();
        navigateHome();
    };

    const navigateHome = () => {
        setTimeout(
            () => {
                history.push(`/enquiry-dashboard-maintenance/dashboard-details?DashboardId=${dashboardId}`);
            },
            0
        );
    };

    const OnSaveDashboardQuickLinks = async () => {
        const filteredQuickLinks = quickLinksGridInnerRef?.current?.getData?.().map(({ QuickLinkId, Sequence }) => ({
            QuickLinkId,
            Sequence
        }));

        const response = await saveDashboardQuickLinksMutateAsync({
            DashboardId: dashboardId,
            DashboardQuickLinks: filteredQuickLinks,
            EnableQuickLinks: isQuickLinksEnabled
        });

        if (response?.Status) navigateHome();
    };

    const setActionBarButtons = React.useCallback(
        async () => {
            setActionBar({
                leftIcons: [{
                    label: 'Back',
                    Icon: ArrowBack,
                    action: 'FieldsBackButtonClick'
                }],
                centerIcons: [],
                rightIcons: [
                    {
                        label: 'Cancel',
                        Icon: CancelIcon,
                        disabled: false,
                        action: 'FieldsOnDiscard',
                        iconStyle: { fill: 'rgba(178, 0, 0, 1)' },
                        callBack: quickLinksGridInnerRef.current?.disableQuickLinksFunctionalities ? handleDiscardOnOkConfirmation : OnDiscardDashboardQuickLinksChanges
                    },
                    {
                        label: 'Ok',
                        Icon: CheckCircle,
                        disabled: quickLinksGridInnerRef.current?.disableQuickLinksFunctionalities,
                        iconStyle: quickLinksGridInnerRef.current?.disableQuickLinksFunctionalities ? undefined : { fill: 'rgba(109, 217, 0, 1)' },
                        action: 'FieldsOnCommit',
                        callBack: OnSaveDashboardQuickLinks
                    },
                ]
            });
        },
        [dashboardId, isQuickLinksEnabled]
    );

    React.useEffect(
        () => {
            setActionBarButtons();
        },
        [setActionBarButtons]
    );

    const ELEMENT2 = 2;

    const handleAddQuickLinksOptions = async () => {
        const selectedItems = fieldsGridInnerRef?.current?.getSelectedRowsData?.() || [];
        const existingItems = quickLinksDataState?.DashboardQuickLinks || [];

        const existingIds = new Set(existingItems.map((item) => item.QuickLinkId));
        const uniqueNewItems = selectedItems.filter((item) => !existingIds.has(item.QuickLinkId));

        if (uniqueNewItems.length === 0) {
            fieldsGridInnerRef?.current?.deselectAll?.();

            return;
        }

        const lastSequence = existingItems.length > 0
            ? Math.max(...existingItems.map((item) => item.Sequence || 0))
            : 0;

        const selectedItemsWithSequence = uniqueNewItems.map((item, index) => ({
            ...item,
            Sequence: lastSequence + index + 1,
        }));

        setDashboardQuickLinksQueryData(Number(dashboardId), {
            DashboardQuickLinks: [...existingItems, ...selectedItemsWithSequence],
        });

        setTimeout(() => { fieldsGridInnerRef?.current?.deselectAll?.(); }, 0);
    };

    return (<>
        <div className={classes.fieldsContainer}>
            <div className={classes.leftContainer}>
                <Paper className={classes.paperLeftContainer}>
                    <Paper className={classes.paperLeft} elevation={0}>
                        <ToggleButton
                            checked={!!isQuickLinksEnabled}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setIsQuickLinksEnabled(e.target.checked);
                              }}
                            label={'Enable quick links menu'}
                            value={'enableQuickLinksMenu'}
                        />
                    </Paper>
                    <Paper className={classes.fieldsLeftContainer} elevation={0}>
                        <Paper className={classes.fieldContainerLabel} elevation={0}>Enquiry Options</Paper>
                        <FieldsGrid innerRef={fieldsGridInnerRef} selectedDashboardType={dashboardType} selectedDashboardId={selectedDashboardId} />
                    </Paper>
                    <div className={classes.buttonsContainer}>
                        {
                            [
                                {
                                    id: 0,
                                    label: 'Add options',
                                    isDefault: true,
                                    listener: handleAddQuickLinksOptions,
                                    // disabled: selectedFieldNames.length === 0
                                },
                            ].map((button) => {
                                return (
                                    <Button
                                        key={button.id}
                                        color='primary'
                                        onClick={() => { button.listener(); }}
                                        autoFocus={button.isDefault}
                                        className={(button.isDefault) ? classes.active : classes.action}
                                        buttonRef={(Ref) => {
                                            if (button.id === 0) {
                                                firstButtonRef.current = Ref;
                                            }
                                            if (button.id === ELEMENT2) {
                                                lastButtonRef.current = Ref;
                                            }
                                        }}
                                    >
                                        {button.label}
                                    </Button>
                                );
                            })
                        }
                    </div>
                </Paper>
            </div>
            <div className={classes.rightContainer}>
                <Paper className={classes.outputFieldsContainer}>
                    <Paper className={classes.containerLabel} elevation={0}>Quick Links</Paper>
                    <QuickLinksGrid innerRef={quickLinksGridInnerRef} setQuickLinksDataState={setQuickLinksDataState} />
                </Paper>
            </div>
        </div>
    </>);
};

export default withRouter(withStyles(styles, { index: 1 })(React.memo(QuickLinks)));
